import { useEffect, useRef, useState, useCallback } from "react";

function useGoogleAutocomplete(inputRef, onSelect) {
  const autocompleteRef = useRef(null);

  const handlePlaceSelect = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.formatted_address) {
      onSelect(place.formatted_address);
    }
  }, [onSelect]);

  useEffect(() => {
    const init = () => {
      if (!window.google?.maps?.places || !inputRef.current) return false;
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["address"],
        componentRestrictions: { country: "us" },
        fields: ["formatted_address", "geometry", "address_components"],
      });
      autocompleteRef.current.addListener("place_changed", handlePlaceSelect);
      return true;
    };

    if (init()) return;

    const interval = setInterval(() => {
      if (init()) clearInterval(interval);
    }, 200);
    return () => clearInterval(interval);
  }, [handlePlaceSelect, inputRef]);
}

function VerifiedBadge() {
  return (
    <span
      data-testid="address-verified-badge"
      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400"
    >
      Verified
    </span>
  );
}

export default function AddressAutocomplete({ id, value, onChange, className }) {
  const inputRef = useRef(null);
  const [verified, setVerified] = useState(false);

  const onSelect = useCallback((address) => {
    onChange({ target: { name: "address", value: address } });
    setVerified(true);
  }, [onChange]);

  useGoogleAutocomplete(inputRef, onSelect);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        name="address"
        autoComplete="street-address"
        aria-label="Service address"
        value={value}
        onChange={(e) => { onChange(e); setVerified(false); }}
        data-testid="contact-input-address"
        placeholder="Start typing your address..."
        className={className}
      />
      {verified && value && <VerifiedBadge />}
    </div>
  );
}
